import { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  Award,
  Bot,
  CheckCircle2,
  Layers,
  MessageSquare,
  Plus,
  RefreshCw,
  Search,
  Sparkles,
  X,
  AlertTriangle,
} from "lucide-react";
import {
  feedbackBatchesListFeedbackBatches,
  feedbackBatchesCreateFeedbackBatch,
  feedbackBatchesArchiveFeedbackBatch,
} from "../gen";
import type { BatchResponse } from "../gen/models";
import { useOrg } from "../hooks/useOrg";
import { useSearchQuery } from "../hooks/useSearchQuery";
import { useFilterParam } from "../hooks/useFilterParam";
import { useToast } from "../context/ToastContext";
import { BatchDetail } from "./BatchDetail";
import { CreateBatchModal } from "../components/batch/CreateBatchModal";
import { BatchArchiveModal } from "../components/batch/BatchReviewModals";
import { PaginationControls } from "../components/common/PaginationControls";
import { ScrollableColumnContainer } from "../components/common/ScrollableColumnContainer";
import { TimeAgo } from "../components/common/TimeAgo";
import { FilterSelect, type FilterSelectOption } from "../components/common/FilterSelect";

const BATCH_STATUS_OPTIONS: FilterSelectOption[] = [
  { value: "collecting", label: "Collecting" },
  { value: "processing", label: "Processing" },
  { value: "failed_processing", label: "Failed Processing" },
  { value: "review_ready", label: "Review Ready" },
  { value: "completed", label: "Completed" },
  { value: "archived", label: "Archived" },
];

const BATCH_CONTENT_OPTIONS: FilterSelectOption[] = [
  { value: "rules", label: "Draft Rules" },
  { value: "knowledge", label: "Draft Knowledge" },
  { value: "evaluations", label: "Evaluations" },
];

export function BatchesWorkspace() {
  const { batch_id } = useParams<{ batch_id?: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const { activeOrgId } = useOrg();

  const [batches, setBatches] = useState<BatchResponse[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [statusFilter, setStatusFilter] = useFilterParam("status", "all");
  const [contentFilter, setContentFilter] = useFilterParam("content", "all");
  const [searchQuery, setSearchQuery] = useSearchQuery("q");

  // New Batch Modal
  const [isNewModalOpen, setIsNewModalOpen] = useState(false);
  const [newTitle, setNewTitle] = useState("");
  const [newDescription, setNewDescription] = useState("");
  const [creatingBatch, setCreatingBatch] = useState(false);

  // Archive Modal State
  const [batchToArchive, setBatchToArchive] = useState<BatchResponse | null>(null);
  const [archiveActionLoading, setArchiveActionLoading] = useState(false);

  const loadBatches = async () => {
    setLoading(true);
    setError(null);
    try {
      const params: any = {
        limit: pageSize,
        offset: (page - 1) * pageSize,
      };
      if (statusFilter !== "all") params.status = statusFilter;
      if (searchQuery.trim()) params.q = searchQuery.trim();

      const res = await feedbackBatchesListFeedbackBatches(params);
      const data = (res as any).data;
      if (data && Array.isArray(data.items)) {
        setBatches(data.items);
        setTotal(data.total ?? data.items.length);
      } else if (Array.isArray(data)) {
        setBatches(data);
        setTotal(data.length);
      } else {
        setBatches([]);
        setTotal(0);
      }
    } catch (err: any) {
      setError(err?.message || "Failed to load feedback batches");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setPage(1);
  }, [statusFilter, searchQuery]);

  useEffect(() => {
    loadBatches();
  }, [statusFilter, searchQuery, page, pageSize, activeOrgId]);

  const handleCreateBatch = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newTitle.trim()) return;
    setCreatingBatch(true);
    try {
      const res = await feedbackBatchesCreateFeedbackBatch({
        title: newTitle.trim(),
        description: newDescription.trim() || undefined,
      });
      const created = (res as any).data as BatchResponse;
      setIsNewModalOpen(false);
      setNewTitle("");
      setNewDescription("");
      await loadBatches();
      toast.success("Feedback batch created successfully.");
      navigate(`/batches/${created.id}${location.search}`);
    } catch (err: any) {
      toast.error("Failed to create batch: " + (err?.message || "Unknown error"));
    } finally {
      setCreatingBatch(false);
    }
  };

  const handleArchiveConfirm = async () => {
    if (!batchToArchive) return;
    setArchiveActionLoading(true);
    try {
      await feedbackBatchesArchiveFeedbackBatch(batchToArchive.id);
      toast.info("Batch has been archived.");
      const archivedId = batchToArchive.id;
      setBatchToArchive(null);
      await loadBatches();
      if (batch_id === archivedId) {
        navigate(`/batches${location.search}`);
      }
    } catch (err: any) {
      toast.error("Failed to archive batch: " + (err?.message || "Unknown error"));
    } finally {
      setArchiveActionLoading(false);
    }
  };

  const filteredBatches = useMemo(() => {
    return batches
      .filter((b) => {
        if (contentFilter === "rules") return (b.draft_rules_count ?? 0) > 0;
        if (contentFilter === "knowledge") return (b.draft_knowledge_items_count ?? 0) > 0;
        if (contentFilter === "evaluations") return (b.evaluations_count ?? 0) > 0;
        return true;
      })
      .sort(
        (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      );
  }, [batches, contentFilter]);
  const hasSelectedItem = Boolean(batch_id && filteredBatches.some((b) => b.id === batch_id));

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "collecting":
        return "bg-stone-100 dark:bg-stone-800 text-stone-700 dark:text-stone-300 border-stone-200 dark:border-stone-700";
      case "processing":
        return "bg-indigo-50 dark:bg-indigo-950/60 text-indigo-700 dark:text-indigo-300 border-indigo-200 dark:border-indigo-800";
      case "failed_processing":
        return "bg-rose-50 dark:bg-rose-950/60 text-rose-700 dark:text-rose-300 border-rose-200 dark:border-rose-800";
      case "review_ready":
        return "bg-amber-50 dark:bg-amber-950/60 text-amber-700 dark:text-amber-300 border-amber-200 dark:border-amber-800 ring-1 ring-amber-500/20";
      case "completed":
        return "bg-emerald-50 dark:bg-emerald-950/60 text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800";
      case "archived":
        return "bg-stone-100 dark:bg-stone-800 text-stone-500 dark:text-stone-400 border-stone-200 dark:border-stone-700";
      default:
        return "bg-stone-100 dark:bg-stone-800 text-stone-600 dark:text-stone-400";
    }
  };

  return (
    <div className="flex h-full w-full overflow-hidden min-h-0">
      {/* ============================================================== */}
      {/* COLUMN 2: Compact Batches Catalog Pane                         */}
      {/* ============================================================== */}
      <div
        className={`${
          batch_id ? "hidden md:flex" : "flex"
        } w-full md:w-80 lg:w-[360px] xl:w-96 shrink-0 h-full border-r border-stone-200 dark:border-stone-800 bg-white/70 dark:bg-stone-900/60 flex-col min-h-0 overflow-hidden select-none transition-all`}
      >
        {/* Pane Header */}
        <div className="p-3.5 border-b border-stone-200 dark:border-stone-800 flex items-center justify-between gap-2 shrink-0 bg-stone-50/50 dark:bg-stone-950/50">
          <div className="flex items-center gap-2 min-w-0">
            <h2 className="text-sm font-bold tracking-tight text-stone-900 dark:text-stone-100 truncate">
              Feedback Batches
            </h2>
            <span className="px-1.5 py-0.2 rounded-md bg-stone-200 dark:bg-stone-800 text-[10px] font-mono font-semibold text-stone-600 dark:text-stone-400">
              {filteredBatches.length}
            </span>
          </div>

          <div className="flex items-center gap-1 shrink-0">
            <button
              type="button"
              onClick={loadBatches}
              disabled={loading}
              className="p-1.5 rounded-lg border border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-900 text-stone-600 dark:text-stone-400 hover:bg-stone-100 dark:hover:bg-stone-800 transition"
              title="Refresh batches list"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
            </button>
            <button
              type="button"
              onClick={() => setIsNewModalOpen(true)}
              className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-stone-900 dark:bg-stone-100 text-white dark:text-stone-900 text-xs font-semibold hover:bg-stone-800 dark:hover:bg-white transition shadow-2xs"
            >
              <Plus className="w-3.5 h-3.5" />
              <span>New Batch</span>
            </button>
          </div>
        </div>

        {/* Search & Filters */}
        <div className="p-2.5 border-b border-stone-100 dark:border-stone-800/80 shrink-0 space-y-2">
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-stone-400 dark:text-stone-500 pointer-events-none" />
            <input
              type="text"
              placeholder="Search batches..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-8 pr-7 py-1.5 text-xs bg-white dark:bg-stone-900 text-stone-900 dark:text-stone-100 placeholder:text-stone-400 dark:placeholder:text-stone-500 border border-stone-200 dark:border-stone-800 focus:outline-hidden focus:ring-1 focus:ring-stone-400 dark:focus:ring-stone-600 font-sans shadow-2xs"
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => setSearchQuery("")}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600 dark:hover:text-stone-300"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            )}
          </div>

          {/* Horizontal Dropdowns: Status -> Contents */}
          <div className="grid grid-cols-2 gap-2">
            <FilterSelect
              label="Status"
              value={statusFilter}
              onChange={setStatusFilter}
              options={BATCH_STATUS_OPTIONS}
            />
            <FilterSelect
              label="Contents"
              value={contentFilter}
              onChange={setContentFilter}
              options={BATCH_CONTENT_OPTIONS}
            />
          </div>
        </div>

        {/* Batches List */}
        <ScrollableColumnContainer>
          {loading ? (
            <div className="p-8 text-center text-xs text-stone-400 flex items-center justify-center gap-2">
              <RefreshCw className="w-3.5 h-3.5 animate-spin" />
              <span>Loading batches...</span>
            </div>
          ) : error ? (
            <div className="p-4 text-center text-xs text-rose-500">{error}</div>
          ) : filteredBatches.length === 0 ? (
            <div className="p-8 text-center space-y-2">
              <Layers className="w-6 h-6 text-stone-300 dark:text-stone-600 mx-auto" />
              <p className="text-xs text-stone-400">No batches match filters</p>
            </div>
          ) : (
            filteredBatches.map((b) => {
              const isSelected = b.id === batch_id;
              const isDimmed = hasSelectedItem && !isSelected;
              const draftCount = (b.draft_rules_count ?? 0) + (b.draft_knowledge_items_count ?? 0);

              return (
                <div
                  key={b.id}
                  onClick={() => navigate(`/batches/${b.id}${location.search}`)}
                  className={`px-4 py-3 border-b border-l-2 text-xs cursor-pointer transition-all duration-150 space-y-2 ${
                    isSelected
                      ? "bg-white dark:bg-stone-900 border-l-stone-900 dark:border-l-stone-100 border-b-stone-200 dark:border-b-stone-800 shadow-2xs opacity-100"
                      : `border-l-transparent border-b-stone-200/80 dark:border-b-stone-800/60 hover:bg-stone-100/70 dark:hover:bg-stone-800/50 ${
                          isDimmed ? "opacity-50 hover:opacity-100" : "opacity-100"
                        }`
                  }`}
                >
                  <div className="flex items-center justify-between gap-1.5">
                    <span className="font-semibold text-[11px] text-stone-900 dark:text-stone-100 line-clamp-1 min-w-0">
                      {b.title}
                    </span>
                    {b.status === "review_ready" && b.is_stale ? (
                      <span
                        className="inline-flex items-center gap-1 px-1.5 py-0.2 rounded text-[10px] font-bold uppercase tracking-wider shrink-0 border bg-rose-50 dark:bg-rose-950/60 text-rose-700 dark:text-rose-300 border-rose-200 dark:border-rose-800 ring-1 ring-rose-500/20"
                        title={b.stale_reason || "Outdated: one or more proposed changes has already been altered by another version"}
                      >
                        <AlertTriangle className="w-2.5 h-2.5 text-rose-500 shrink-0" />
                        <span>Outdated</span>
                      </span>
                    ) : (
                      <span
                        className={`px-1.5 py-0.2 rounded text-[10px] font-semibold uppercase tracking-wider shrink-0 border ${getStatusBadge(
                          b.status
                        )}`}
                      >
                        {b.status.replace("_", " ")}
                      </span>
                    )}
                  </div>

                  {b.description && (
                    <p className="line-clamp-2 text-[11px] text-stone-500 dark:text-stone-400 leading-snug">
                      {b.description}
                    </p>
                  )}

                  <div className="flex items-center justify-between text-[10px] text-stone-400 dark:text-stone-500 pt-1.5 border-t border-stone-100 dark:border-stone-800/60 gap-1.5">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="inline-flex items-center gap-1 font-mono shrink-0" title="Linked conversations">
                        <Bot className="w-3 h-3 text-stone-400" />
                        <span>{b.threads_count ?? 0}</span>
                      </span>
                      <span className="inline-flex items-center gap-1 font-mono shrink-0" title="Comments">
                        <MessageSquare className="w-3 h-3 text-stone-400" />
                        <span>{b.comments_count ?? 0}</span>
                      </span>
                      <span className="inline-flex items-center gap-1 font-mono shrink-0" title="Evaluations">
                        <Award className="w-3 h-3 text-stone-400" />
                        <span>{b.evaluations_count ?? 0}</span>
                      </span>
                      {draftCount > 0 && (
                        b.status === "completed" ? (
                          <span className="inline-flex items-center gap-0.5 text-emerald-600 dark:text-emerald-400 font-semibold shrink-0">
                            <CheckCircle2 className="w-2.5 h-2.5" />
                            <span>{draftCount}</span>
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-0.5 text-amber-600 dark:text-amber-400 font-semibold shrink-0">
                            <Sparkles className="w-2.5 h-2.5" />
                            <span>{draftCount}</span>
                          </span>
                        )
                      )}
                      {(b.created_by_user_name || b.created_by_user_id) && (
                        <>
                          <span>•</span>
                          <span
                            className="truncate text-stone-500 dark:text-stone-400"
                            title={`Created by ${b.created_by_user_name || b.created_by_user_id}`}
                          >
                            by {b.created_by_user_name || b.created_by_user_id}
                          </span>
                        </>
                      )}
                    </div>

                    <TimeAgo date={b.created_at} className="shrink-0 font-mono" />
                  </div>
                </div>
              );
            })
          )}
        </ScrollableColumnContainer>

        {/* Pagination Controls */}
        <PaginationControls
          compact
          page={page}
          pageSize={pageSize}
          total={total}
          onPageChange={setPage}
          onPageSizeChange={(newSize) => {
            setPageSize(newSize);
            setPage(1);
          }}
          disabled={loading}
        />
      </div>

      {/* ============================================================== */}
      {/* COLUMN 3: Detail Workspace Pane                                */}
      {/* ============================================================== */}
      <div
        className={`${
          batch_id ? "flex" : "hidden md:flex"
        } flex-1 min-w-0 h-full overflow-hidden flex-col bg-stone-50 dark:bg-stone-950`}
      >
        {batch_id ? (
          <BatchDetail key={batch_id} batchId={batch_id} onBatchUpdated={loadBatches} />
        ) : (
          <div className="h-full w-full flex flex-col items-center justify-center p-8 text-center select-none space-y-4">
            <div className="w-12 h-12 rounded-2xl bg-white dark:bg-stone-900 border border-stone-200 dark:border-stone-800 flex items-center justify-center shadow-xs">
              <Layers className="w-6 h-6 text-stone-400 dark:text-stone-500" />
            </div>
            <div className="space-y-1 max-w-sm">
              <h3 className="text-base font-bold text-stone-900 dark:text-stone-100">
                Select a Feedback Batch
              </h3>
              <p className="text-xs text-stone-500 dark:text-stone-400 leading-relaxed">
                Choose a feedback batch from the catalog to review curated comments, inspect synthesized
                rules and knowledge diffs, and simulate or promote updates.
              </p>
            </div>
            <div className="flex items-center gap-2 pt-2">
              {filteredBatches.length > 0 && (
                <button
                  type="button"
                  onClick={() => navigate(`/batches/${filteredBatches[0].id}${location.search}`)}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-stone-200 dark:border-stone-800 bg-white dark:bg-stone-900 text-xs font-semibold text-stone-700 dark:text-stone-300 hover:bg-stone-50 dark:hover:bg-stone-800 transition shadow-2xs"
                >
                  <span>View Latest ({filteredBatches[0].title})</span>
                </button>
              )}
              <button
                type="button"
                onClick={() => setIsNewModalOpen(true)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-stone-900 dark:bg-stone-100 text-white dark:text-stone-900 text-xs font-semibold hover:bg-stone-800 dark:hover:bg-white transition shadow-2xs"
              >
                <Plus className="w-3.5 h-3.5" />
                <span>New Batch</span>
              </button>
            </div>
          </div>
        )}
      </div>

      {/* New Batch Modal */}
      <CreateBatchModal
        isOpen={isNewModalOpen}
        newTitle={newTitle}
        newDescription={newDescription}
        creatingBatch={creatingBatch}
        onClose={() => setIsNewModalOpen(false)}
        onTitleChange={setNewTitle}
        onDescriptionChange={setNewDescription}
        onSubmit={handleCreateBatch}
      />

      {/* Archive Batch Modal */}
      <BatchArchiveModal
        isOpen={Boolean(batchToArchive)}
        onClose={() => setBatchToArchive(null)}
        onConfirm={handleArchiveConfirm}
        actionLoading={archiveActionLoading}
        batchTitle={batchToArchive?.title}
      />
    </div>
  );
}
